import { Text } from "components/Typography/text.styled";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  AGIActionContainer,
  AGIBody,
  AGICell,
  AGIDivider,
  AGITaxContainer,
  AGIWorkerContainer,
  EmployerTaxContainer,
  GrossContainer,
  PersonalIdentityNumberContainer,
} from "./salary.styled";
import { FontFamily, FontSize } from "config/font";
import { Color } from "config/colors";
import { Api } from "services/api/api.service";
import { LocalDate } from "@js-joda/core";
import { Pagination } from "../general/pagination.component";
import { CustomIcon } from "components/icon/customIcon.component";
import { AbsenceDetailRow } from "./absenceDetailRow.component";
import { PayoutDetailRow } from "./payoutDetailRow.component";
import React from "react";
import { workersAGI } from "model/Salary";

interface AGIListProps {
  currentDate: LocalDate;
  data: workersAGI[];
  setData: React.Dispatch<React.SetStateAction<workersAGI[]>>;
}

export interface DetailsState {
  isOpen: boolean;
  selectedIndex: number | null;
}

export const AGIList: React.FC<AGIListProps> = ({
  currentDate,
  data,
  setData,
}) => {
  const { t } = useTranslation();
  const [{ company }] = useCompanyStore();
  const itemsPerPage = 25;
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [allWorkers, setAllWorkers] = useState<workersAGI[]>([]);
  const [detailsState, setDetailsState] = useState<DetailsState>({
    isOpen: false,
    selectedIndex: null,
  });
  const currency = "SEK";

  const fetchAGIData = useCallback(() => {
    if (!company?.id) return;

    Api()
      .company.salary.getWorkersAGI(
        company.id,
        currentDate.year(),
        currentDate.monthValue()
      )
      .then((res) => {
        setAllWorkers(res.data.workers);
        setTotalPages(Math.ceil(res.data.workers.length / itemsPerPage));

        const firstPageData = res.data.workers.slice(0, itemsPerPage);
        setData(firstPageData);
      })
      .catch((e) => {
        console.error("Error when getting individual agi", e);
      });
  }, [company?.id, currentDate, setData, itemsPerPage]);

  const handlePageChange = useCallback(
    (newPageOrUpdater: React.SetStateAction<number>) => {
      const newPage =
        typeof newPageOrUpdater === "function"
          ? newPageOrUpdater(page)
          : newPageOrUpdater;

      setPage(newPage);

      const startIndex = newPage * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;

      const pageData = allWorkers.slice(startIndex, endIndex);
      setData(pageData);

      setDetailsState({
        isOpen: false,
        selectedIndex: null,
      });
    },
    [allWorkers, itemsPerPage, setData, page]
  );

  const toggleDetails = useCallback((index: number) => {
    setDetailsState((prevState) => {
      if (prevState.selectedIndex === index) {
        return {
          isOpen: !prevState.isOpen,
          selectedIndex: index,
        };
      }

      return {
        isOpen: true,
        selectedIndex: index,
      };
    });
  }, []);

  const isDetailsVisible = useCallback(
    (index: number) => {
      return detailsState.isOpen && detailsState.selectedIndex === index;
    },
    [detailsState]
  );

  useEffect(() => {
    if (data.length <= 0) {
      fetchAGIData();
    }
  }, [data, fetchAGIData]);

  const renderAGIRow = useCallback(
    (agi: workersAGI, index: number) => {
      const detailsOpen = isDetailsVisible(index);

      return (
        <React.Fragment key={`agi-worker-${index}`}>
          <AGICell
            onClick={() => toggleDetails(index)}
            key={`agi-summary-${index}`}
          >
            <AGIWorkerContainer>
              <Text
                style={{ margin: 0, padding: 0 }}
                fontSize={FontSize.Small}
                color={Color.SeaBlue600}
              >
                {t("Salary.Worker")}
              </Text>
              <Text
                color={Color.MidnightBlue}
                fontFamily={FontFamily.MontserratRegular}
              >
                {`${agi.firstName} ${agi.lastName}`}
              </Text>
            </AGIWorkerContainer>
            <PersonalIdentityNumberContainer>
              <Text
                style={{ margin: 0, padding: 0 }}
                fontSize={FontSize.Small}
                color={Color.SeaBlue600}
              >
                {t("Salary.PersonalIdentityNumber")}
              </Text>
              <Text
                color={Color.MidnightBlue}
                fontFamily={FontFamily.MontserratRegular}
              >
                {agi.personalIdentityNumber}
              </Text>
            </PersonalIdentityNumberContainer>
            <AGIDivider />
            <GrossContainer>
              <Text
                style={{ margin: 0, padding: 0 }}
                fontSize={FontSize.Small}
                color={Color.SeaBlue600}
              >
                {t("Salary.GrossPayoutAmount")}
              </Text>
              <Text
                color={Color.MidnightBlue}
                fontFamily={FontFamily.MontserratRegular}
              >
                {`${agi.totalGrossPayoutAmount} ${currency}`}
              </Text>
            </GrossContainer>
            <EmployerTaxContainer>
              <Text
                style={{ margin: 0, padding: 0 }}
                fontSize={FontSize.Small}
                color={Color.SeaBlue600}
              >
                {t("Salary.EmployerTax")}
              </Text>
              <Text
                color={Color.MidnightBlue}
                fontFamily={FontFamily.MontserratRegular}
              >
                {`${agi.totalEmployerTax} ${currency}`}
              </Text>
            </EmployerTaxContainer>
            <AGITaxContainer>
              <Text
                style={{ margin: 0, padding: 0 }}
                fontSize={FontSize.Small}
                color={Color.SeaBlue600}
              >
                {t("Salary.Tax")}
              </Text>
              <Text
                color={Color.MidnightBlue}
                fontFamily={FontFamily.MontserratRegular}
              >
                {`${agi.totalWorkerTax} ${currency}`}
              </Text>
            </AGITaxContainer>
            <AGIActionContainer>
              <CustomIcon
                name={detailsOpen ? "upward_arrow" : "downward_arrow"}
                size="24px"
                color={Color.BurntSienna}
              />
            </AGIActionContainer>
          </AGICell>

          {detailsOpen && (
            <AGICell
              style={{
                display: "flex",
                flexDirection: "column",
              }}
              key={`agi-details-${index}`}
            >
              <PayoutDetailRow
                payoutDetails={agi.payoutDetails}
                currency={currency}
              />
              {agi.absences.length > 0 && (
                <AbsenceDetailRow absences={agi.absences} />
              )}
            </AGICell>
          )}
        </React.Fragment>
      );
    },
    [t, currency, toggleDetails, isDetailsVisible]
  );

  return (
    <AGIBody>
      {data.map(renderAGIRow)}
      <Pagination
        setPage={handlePageChange}
        page={page}
        totalPages={totalPages}
      />
    </AGIBody>
  );
};
